import { BottomNavigation } from '@/components/Layout/BottomNavigation'
import { Header } from '@/components/Layout/Header'
import { ArPhotoOverlay } from '@/components/Models/ar-photo'
import { DialogArPhotoLocked, DialogAudioGuideLocked, DialogSpotsAudioGuide } from '@/components/Pages/spots/components'
import { DialogIlustmap } from '@/components/Pages/spots/components/list/DialogIlustmap'
import { SpotFilterRow } from '@/components/Pages/spots/components/list/SpotFilterRow'
import { SpotFilterRowSkeleton } from '@/components/Pages/spots/components/list/SpotFilterRowSkeleton'
import { SpotList } from '@/components/Pages/spots/components/list/SpotList'
import { SpotListSkeleton } from '@/components/Pages/spots/components/list/SpotListSkeleton'
import { SpotMapLoadFailed } from '@/components/Pages/spots/components/list/SpotMapLoadFailed'
import { SpotMapSkeleton } from '@/components/Pages/spots/components/list/SpotMapSkeleton'
import type { PillSelectOption } from '@/components/UI/PillSelect'
import { Tabs } from '@/components/UI/Tabs'
import { useApi } from '@/core/api/useApi'
import { useCampaignSlug } from '@/core/app/useCampaignSlug'
import { AppMachineContext } from '@/core/app/useRaundLifeCycle'
import { resolveGeoLocationPolicy } from '@/core/geolocation/geoLocationPolicy'
import { postWithLocationRetry } from '@/core/geolocation/locationRetry'
import useGeoLocation from '@/core/geolocation/useGeoLocation'
import { useNotificationBadge } from '@/domain/popup/useNotificationBadge'
import { type SpotTab, buildSpotTabUrl } from '@/domain/spot/spotTabQuery'
import { useStampPointLabelFromSheetTypes } from '@/domain/stamp/useStampPointLabel'
import { CAMPAIGN_LIST_TYPE } from '@/schemas/v2/campaign'
import {
  type PhotoFrameItem,
  type PhotoFrameLockReason,
  type SpotData,
  isPhotoFrameLocked,
  needsLocationForPhotoFrames,
  pickPhotoFrameAudio,
  resolvePhotoFrameLockReason,
  toPhotoFrameItems,
} from '@/schemas/v2/spot'
import { useTokenStore } from '@/store/useTokenStore'
import { useSelector } from '@xstate/react'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSpotListStateMachine } from './useSpotListStateMachine'

// Leaflet は window に依存するため、ビルド時の Node.js 実行を回避するためにブラウザ側のみロードする
const SpotMap = dynamic(() => import('@/components/Pages/spots/components/list/SpotMap').then((m) => m.SpotMap), {
  ssr: false,
  // チャンクの読み込みに失敗したときはスケルトンを出し続けず、失敗の案内と再試行ボタンを出す（#3625）。
  // スポット取得中のオーバーレイ（spot-map-skeleton）と同時に存在し得るため、testid を分ける。
  // キャンペーン・スポットの取得が先に終わりチャンクだけ読み込み中の間は main の aria-busy が false になるため、
  // このプレースホルダー自身が読み込み中を支援技術に伝える
  loading: ({ error, retry }) =>
    error ? (
      <SpotMapLoadFailed onRetry={retry} />
    ) : (
      <SpotMapSkeleton testId="spot-map-chunk-skeleton" announceLoading />
    ),
})

type Props = {
  /**
   * URL クエリ ?tab= が指すタブ（#3665）。マップ / リストのタブ状態はこの値だけで決まる。
   * 履歴エントリごとにクエリが残るため、詳細からのブラウザバックや一覧同士の戻る / 進むでも URL と表示が一致する。
   * hydration 前（router.isReady が false）は query が空のため 0 になり、確定後に本来の値へ変わる。
   */
  queryTab: SpotTab
}

export const SpotListPage = ({ queryTab }: Props) => {
  const { t } = useTranslation()
  const router = useRouter()
  const { gen } = useCampaignSlug()

  const appActorRef = AppMachineContext.useActorRef()
  const isAuthenticated = useSelector(appActorRef, (s) => s.context.isAuthenticated)
  const campaignData = useSelector(appActorRef, (s) => s.context.campaignData)
  const { hasUnread } = useNotificationBadge(isAuthenticated)

  // マップなし表示（listType = LIST）では表示時に位置情報を取得しない（#3583）。
  // campaignData が確定するまでは pending にして、ロード前に許可プロンプトが出ないようにする。
  // ARフォト／音声ガイドで座標が必要になったときだけ requestLocation() で測位する。
  const geoLocation = useGeoLocation({ policy: resolveGeoLocationPolicy(campaignData) })

  const campaignSheetTypes = campaignData?.sheetTypes ?? []
  const { label: stampPointLabel } = useStampPointLabelFromSheetTypes(campaignSheetTypes)

  const { list, attached } = useSpotListStateMachine({
    isAuthenticated,
    defaultSheetId: campaignData?.defaultSheetId ?? null,
  })

  const api = useApi()
  const idToken = useTokenStore((s) => s.idToken)
  const [arPhotoFrames, setArPhotoFrames] = useState<PhotoFrameItem[]>([])
  // ARフォトのロック理由（#3445）。文言をスタンプ未獲得 / 範囲外で出し分けるために保持する
  const [arPhotoLockReason, setArPhotoLockReason] = useState<PhotoFrameLockReason | null>(null)
  // ロック時に「端末の位置情報設定をご確認ください」を出すか。このタップで測位を試みて失敗したときだけ true（#3583）
  const [arPhotoLocationUnavailable, setArPhotoLocationUnavailable] = useState(false)
  // AR起動時に自動再生する音声URL（#3261）。署名付きURLのためキャッシュせず起動直前の取得結果から保持する
  const [arPhotoAudio, setArPhotoAudio] = useState<string | null>(null)
  // ARフォトAPI呼び出し中の二重送信防止（描画不要なため useRef で管理）。
  // 一覧は複数スポットが並ぶため、スポット単位で保持しないと別スポットのタップまで握り潰す
  const arPhotoInFlightRef = useRef<Set<number>>(new Set())
  const [isMapDrawerOpen, setIsMapDrawerOpen] = useState(false)

  // GPS 更新等で頻繁に再レンダーされるページのため、参照を安定化して
  // memo された ArPhotoOverlay の不要な再レンダーを抑える。
  const handleArPhotoClose = useCallback(() => {
    list.send({ type: 'CLOSE_AR_PHOTO' })
    setArPhotoFrames([])
  }, [list.send])

  const illustrationMaps = campaignData?.illustrationMaps ?? []
  const hasImageMap = illustrationMaps.length > 0
  const imageMapLabel = campaignData?.mapCaption || 'PDF'

  // campaignData が null（ロード中）のうちは showMap / showList を false にする。
  // ロード前に SpotMap（Leaflet）がマウントされると dynamic import が不要に走るため、
  // ロード完了後に listType に応じて正しい表示を確定させる。
  const isCampaignLoaded = campaignData !== null
  const listType = campaignData?.listType ?? CAMPAIGN_LIST_TYPE.MAP_AND_LIST
  const showMap = isCampaignLoaded && listType !== CAMPAIGN_LIST_TYPE.LIST
  const showList = isCampaignLoaded && listType !== CAMPAIGN_LIST_TYPE.MAP
  // マップ・リスト両方表示、またはイラストマップが存在する場合にタブを表示する
  const hasTabs = (showMap && showList) || (isCampaignLoaded && hasImageMap)
  // マップ / リストを切り替えられるのは MAP_AND_LIST のみ。それ以外で ?tab=list が付いていても先頭タブのままにする
  const activeTab: SpotTab = listType === CAMPAIGN_LIST_TYPE.MAP_AND_LIST ? queryTab : 0
  // Static Export では hydration が終わるまで query が空で、?tab=list のフルロードでも一度マップ扱いになる。
  // 確定前にマップ（MapLibre のチャンク）を誤ってマウントしないよう、確定までは取得中と同じスケルトンを出す
  const isTabResolved = router.isReady

  const tabs = useMemo<string[]>(() => {
    if (listType === CAMPAIGN_LIST_TYPE.MAP_AND_LIST) {
      return hasImageMap
        ? [t('spot.map', 'マップ'), t('spot.list', 'リスト'), imageMapLabel]
        : [t('spot.map', 'マップ'), t('spot.list', 'リスト')]
    }
    if (listType === CAMPAIGN_LIST_TYPE.MAP) {
      return hasImageMap ? [t('spot.map', 'マップ'), imageMapLabel] : []
    }
    // CAMPAIGN_LIST_TYPE.LIST
    return hasImageMap ? [t('spot.list', 'リスト'), imageMapLabel] : []
  }, [listType, hasImageMap, imageMapLabel, t])

  // ARフォトボタン押下時（#3445 / #3583）:
  //   1. 取得済みの座標があれば付けて、なければ座標なしで POST（位置情報が取れていなくても起動を試みる）
  //   2. サーバーが location_required（座標があれば結果が変わる）を返したときだけ測位して再 POST
  //   3. 結果に応じてフレームをセット、またはロックダイアログを表示
  // ログイン済みの場合は Authorization ヘッダー付き (api.auth) で呼び出し、バックエンドがスタンプ取得状況を確認できるようにする。
  const handleArPhotoClick = async (spot: SpotData) => {
    if (arPhotoInFlightRef.current.has(spot.id)) return
    arPhotoInFlightRef.current.add(spot.id)
    try {
      const { response: res, locationDenied } = await postWithLocationRetry(
        (body) =>
          idToken !== null ? api.auth.postSpotPhotoFrames(spot.id, body) : api.guest.postSpotPhotoFrames(spot.id, body),
        (r) => r !== null && needsLocationForPhotoFrames(r.photoFrames),
        geoLocation,
      )
      if (!res) {
        // 理由を特定できないため既定（スタンプ未獲得）の文言にフォールバックする
        setArPhotoLockReason(null)
        setArPhotoLocationUnavailable(false)
        attached.send({ type: 'OPEN_AR_PHOTO_LOCKED' })
        return
      }
      const entries = res.photoFrames
      if (isPhotoFrameLocked(entries)) {
        setArPhotoLockReason(resolvePhotoFrameLockReason(entries))
        setArPhotoLocationUnavailable(locationDenied)
        attached.send({ type: 'OPEN_AR_PHOTO_LOCKED' })
      } else {
        setArPhotoFrames(toPhotoFrameItems(entries))
        setArPhotoAudio(pickPhotoFrameAudio(entries))
        list.send({ type: 'OPEN_AR_PHOTO', spot })
      }
    } catch {
      // 理由を特定できないため既定（スタンプ未獲得）の文言にフォールバックする
      setArPhotoLockReason(null)
      setArPhotoLocationUnavailable(false)
      attached.send({ type: 'OPEN_AR_PHOTO_LOCKED' })
    } finally {
      arPhotoInFlightRef.current.delete(spot.id)
    }
  }

  // PillSelect に渡す options（「すべてのコース」を先頭に追加）。value はシートID文字列。
  // 購入連動ロック中（locked=true）のシートには 🔓 を付与する。
  const sheetOptions = useMemo<PillSelectOption[]>(
    () => [
      { value: '', label: t('spot.all_courses') },
      ...list.context.sheetFilterOptions.map((s) => ({
        value: String(s.id),
        label: s.locked ? `🔓 ${s.name}` : s.name,
      })),
    ],
    [list.context.sheetFilterOptions, t],
  )

  // #3414: 未購入スポットでも詳細ページへ遷移させる（ロックの提示・注意喚起はスポット詳細に一元化）。
  // カードの灰色表示も行わず、購入前であることはシート名の 🔓 のみで示す。

  // マップ / リスト タブは URL クエリ ?tab=list で切り替える（#3665）。
  // 詳細からブラウザバックで戻ったとき、履歴エントリに残ったクエリから同じタブを復元するため。
  // 履歴エントリを増やさないよう replace + shallow で現在のエントリに書く
  const selectTab = (tab: SpotTab) => {
    // 選択中のタブの再タップでは URL が変わらないため、ルーター更新（アプリ全体の再レンダー）を起こさない
    if (tab === activeTab) return
    // 実際のアドレスから URL を組み立てる。本番のシェル HTML では router.query.campaign が空のことがあり、
    // router.pathname（/[campaign]/spots）と query から組み立てるとスラッグを補間できない
    const url = buildSpotTabUrl(window.location.pathname + window.location.search, tab)
    void router.replace(url, undefined, { shallow: true })
  }

  const handleTabChange = (index: number) => {
    if (listType === CAMPAIGN_LIST_TYPE.MAP_AND_LIST) {
      if (hasImageMap && index === 2) list.send({ type: 'SHOW_PDF' })
      else selectTab(index === 0 ? 0 : 1)
    } else if (listType === CAMPAIGN_LIST_TYPE.MAP) {
      // tab 0 = マップ（state変更不要）、tab 1 = イラストマップ
      if (hasImageMap && index === 1) list.send({ type: 'SHOW_PDF' })
    } else {
      // CAMPAIGN_LIST_TYPE.LIST: tab 0 = リスト（state変更不要）、tab 1 = イラストマップ
      if (hasImageMap && index === 1) list.send({ type: 'SHOW_PDF' })
    }
  }

  // マップ Drawer 展開中、またはルートマップ(PDF)ダイアログ表示中は、
  // 地図上に重なるフィルター行（コース選択・タグ Pill）を操作不可にする
  const isFilterRowBlocked = isMapDrawerOpen || list.context.showPdfDialog

  return (
    <>
      <Header
        title={t('common.spot', 'スポット')}
        showLanguage
        onNotification={() => router.push(gen('/news'))}
        hasUnreadBadge={hasUnread}
      />
      {hasTabs && <Tabs tabs={tabs} activeIndex={activeTab} onChange={handleTabChange} />}
      <main
        style={{ height: hasTabs ? 'calc(100dvh - 116px - 45px)' : 'calc(100dvh - 116px)', paddingBottom: 0 }}
        className="relative w-full overflow-hidden"
        // スケルトンは aria-hidden のため、支援技術には aria-busy で読み込み中を伝える
        aria-busy={!isCampaignLoaded || !isTabResolved || list.isLoading}
      >
        {!isCampaignLoaded || !isTabResolved ? (
          // 表示方法（listType）とタブ（?tab=）が確定するまでは、既定（マップ + リスト）に合わせて全面スケルトンを出す（#3625）
          <SpotMapSkeleton />
        ) : showMap && (listType !== CAMPAIGN_LIST_TYPE.MAP_AND_LIST || !hasTabs || activeTab === 0) ? (
          <>
            {/* SpotMap は isLoading と独立してマウントし、タイル読み込みを先に進める。
                スポットマーカーは API 完了後（isLoading=false）に追加される。 */}
            <SpotMap
              spots={list.isLoading ? [] : list.context.filteredSpots}
              onArPhotoClick={handleArPhotoClick}
              geoLocation={geoLocation}
              stampImageBySpotId={list.context.stampImageBySpotId}
              mapCenter={campaignData?.mapCenter}
              mapPinImage={campaignData?.mapPinImage}
              acquiredSpotIds={list.context.acquiredSpotIds}
              onDrawerOpenChange={setIsMapDrawerOpen}
              sheetNameById={list.sheetNameById}
            />
            {/* 取得中は地図エリア全面をスケルトンで覆う（クルクルローダーは出さない・#3625） */}
            {list.isLoading && <SpotMapSkeleton />}
            {/* フィルター行：ARフォトが非表示の場合のみ地図の上にオーバーレイ表示 */}
            {!list.isLoading && list.context.arPhotoSpot === null && (
              <div
                className={`absolute left-0 top-0 w-full ${isFilterRowBlocked ? 'pointer-events-none z-0' : 'z-[1000]'}`}
              >
                <SpotFilterRow
                  sheetOptions={sheetOptions}
                  selectedSheet={list.context.selectedSheet}
                  onSheetChange={list.selectSheet}
                  tags={list.context.uniqueTags}
                  selectedTagId={list.context.selectedTagId}
                  onTagClick={(tagId) => list.send({ type: 'SELECT_TAG', tagId })}
                />
              </div>
            )}
          </>
        ) : (
          <div className="flex h-full flex-col overflow-hidden">
            {/* 取得中のスケルトンは実表示と同じ入れ子に置き、切り替わり時にフィルター行・カードの位置が動かないようにする（#3625） */}
            {list.isLoading ? (
              <SpotFilterRowSkeleton />
            ) : (
              <SpotFilterRow
                sheetOptions={sheetOptions}
                selectedSheet={list.context.selectedSheet}
                onSheetChange={list.selectSheet}
                tags={list.context.uniqueTags}
                selectedTagId={list.context.selectedTagId}
                onTagClick={(tagId) => list.send({ type: 'SELECT_TAG', tagId })}
              />
            )}
            <div className="min-h-0 flex-1 overflow-hidden">
              {list.isLoading ? (
                <SpotListSkeleton />
              ) : (
                <SpotList
                  spots={list.context.filteredSpots}
                  onArPhotoClick={handleArPhotoClick}
                  geoLocation={geoLocation}
                  sheetNameById={list.sheetNameById}
                />
              )}
            </div>
          </div>
        )}
      </main>
      {list.context.showPdfDialog && (
        <DialogIlustmap
          onClose={() => list.send({ type: 'CLOSE_PDF' })}
          scrollLock
          title={imageMapLabel}
          ilustMaps={(campaignData?.illustrationMaps ?? []).map((m) => ({
            caption: m.name ?? m.url,
            url: m.url,
          }))}
        />
      )}
      <BottomNavigation activeItem="spot" />
      <ArPhotoOverlay
        open={list.context.arPhotoSpot !== null}
        photoFrames={arPhotoFrames}
        audioUrl={arPhotoAudio}
        onClose={handleArPhotoClose}
        snsHashtag=""
      />
      {/* ── 付帯ダイアログ（ARフォトロック・音声ガイド） ── */}
      {attached.isArPhotoLocked && (
        <DialogArPhotoLocked
          scrollLock
          stampLabel={stampPointLabel}
          reason={arPhotoLockReason}
          locationUnavailable={arPhotoLocationUnavailable}
          onClose={() => attached.send({ type: 'CLOSE' })}
        />
      )}
      {attached.isAudioGuide && <DialogSpotsAudioGuide scrollLock onClose={() => attached.send({ type: 'CLOSE' })} />}
      {attached.isAudioGuideLocked && (
        <DialogAudioGuideLocked scrollLock onClose={() => attached.send({ type: 'CLOSE' })} />
      )}
    </>
  )
}
